import React from 'react';
import { Button } from '@/features/ui/components/Button';
import { Combobox } from '@/features/ui/components/Combobox';
import { Input } from '@/features/ui/components/Input';
import { Checkbox } from '@/features/ui/components/Checkbox';
import { Switch } from '@/features/ui/components/Switch';
import { Locator } from '@/features/application/types/forms';
import { useLocatorBuilder } from '../hooks/use-locator-builder.hook';

interface LocatorBuilderProps {
  objectType: string;
  onSave: (locator: Locator) => void;
  onCancel: () => void;
  initialMode?: 'fieldValue' | 'positional';
  initialFieldApiName?: string;
  initialValue?: string | boolean;
  initialSortField?: string;
  initialSortDirection?: 'DESC' | 'ASC';
  initialIndex?: number;
}

export const LocatorBuilder: React.FC<LocatorBuilderProps> = ({
  objectType,
  onSave,
  onCancel,
  initialMode = 'fieldValue',
  initialFieldApiName = '',
  initialValue = '',
  initialSortField = '',
  initialSortDirection = 'DESC',
  initialIndex = 0,
}) => {
  const {
    mode,
    selectedFieldApiName,
    selectedValue,
    sortField,
    sortDirection,
    index,
    isLoading,
    selectedField,
    adaptedType,
    filterableFields,
    sortableFields,
    canSave,
    setMode,
    setSelectedFieldApiName,
    setSelectedValue,
    setSortField,
    setSortDirection,
    setIndex,
    handleSave,
  } = useLocatorBuilder({
    objectType,
    initialMode,
    initialFieldApiName,
    initialValue,
    initialSortField,
    initialSortDirection,
    initialIndex,
  });

  return (
    <div className="space-y-6">
      {/* Strategy Selection - Segmented Control */}
      <div className="flex items-center justify-center">
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
          <label className="cursor-pointer">
            <input
              type="radio"
              name="locatorStrategy"
              value="fieldValue"
              checked={mode === 'fieldValue'}
              onChange={() => setMode('fieldValue')}
              className="sr-only"
            />
            <span
              className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                mode === 'fieldValue'
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Filter by field value
            </span>
          </label>
          <label className="cursor-pointer">
            <input
              type="radio"
              name="locatorStrategy"
              value="positional"
              checked={mode === 'positional'}
              onChange={() => setMode('positional')}
              className="sr-only"
            />
            <span
              className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                mode === 'positional'
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Pick record by order
            </span>
          </label>
        </div>
      </div>

      <div className="border-t pt-4">
        {/* Field Value Strategy - Only show when selected */}
        {mode === 'fieldValue' && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Field Value Filter</h4>
            <Combobox
              options={filterableFields.map((f) => ({
                value: f.id,
                label: `${f.label} (${f.id})`,
              }))}
              value={selectedFieldApiName}
              onChange={setSelectedFieldApiName}
              placeholder={isLoading ? 'Loading fields...' : 'Select field...'}
              searchPlaceholder="Search fields..."
              noResultsMessage="No fields found."
            />
            {selectedField && (
              <div>
                {adaptedType === 'boolean' && (
                  <label className="flex items-center space-x-2">
                    <Checkbox
                      checked={Boolean(selectedValue)}
                      onCheckedChange={(checked) => setSelectedValue(!!checked)}
                    />
                    <span>{selectedField.label}</span>
                  </label>
                )}
                {adaptedType === 'picklist' && (
                  <Combobox
                    options={
                      selectedField.picklistValues?.map((pv) => ({
                        value: pv.value,
                        label: pv.label,
                      })) || []
                    }
                    value={typeof selectedValue === 'string' ? (selectedValue as string) : ''}
                    onChange={(val) => setSelectedValue(val)}
                    placeholder="Select value"
                    searchPlaceholder="Search values..."
                  />
                )}
                {adaptedType === 'text' && (
                  <Input
                    value={typeof selectedValue === 'string' ? (selectedValue as string) : ''}
                    onChange={(e) => setSelectedValue(e.target.value)}
                    placeholder="Enter value"
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* Positional Strategy - Only show when selected */}
        {mode === 'positional' && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Positional Selection</h4>
            <Combobox
              options={sortableFields.map((f) => ({ value: f.id, label: `${f.label} (${f.id})` }))}
              value={sortField}
              onChange={(val) => setSortField(val)}
              placeholder={isLoading ? 'Loading fields...' : 'Sort by field...'}
              searchPlaceholder="Search fields..."
              noResultsMessage="No fields found."
            />
            <div className="flex items-center space-x-2">
              <span className="text-sm">Ascending</span>
              <Switch
                checked={sortDirection === 'DESC'}
                onCheckedChange={(checked) => setSortDirection(checked ? 'DESC' : 'ASC')}
              />
              <span className="text-sm">Descending</span>
            </div>
            <Input
              type="number"
              min={0}
              value={index.toString()}
              onChange={(e) => setIndex(Number(e.target.value))}
              placeholder="Position (0 = first)"
            />
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => handleSave(onSave)} disabled={!canSave}>
          Save
        </Button>
      </div>
    </div>
  );
};
